"use client";

import { useCallback, useState } from "react";

const STORAGE_KEY = "spark-recent-searches";
const MAX_RECENT = 5;
/** Below this a term is more noise than shortcut in the empty state. */
const MIN_LENGTH = 2;

function readStorage(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/**
 * The last few search terms, most recent first, in localStorage.
 *
 * Mirrors useRecentClients: per browser, not synced, and deliberately not
 * server-side. Search terms are a record of what someone was looking into, so
 * keeping them local avoids storing that against the account for no benefit.
 */
export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<string[]>(readStorage);

  const recordSearch = useCallback((raw: string) => {
    const term = raw.trim();
    if (term.length < MIN_LENGTH) return;
    setRecentSearches((prev) => {
      const next = [term, ...prev.filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable (private mode): the list still works this session.
      }
      return next;
    });
  }, []);

  const clearSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { recentSearches, recordSearch, clearSearches };
}
